import { useEffect, useMemo, useState } from "react";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { FormControlLabel, FormHelperText, Radio, RadioGroup, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import Autocomplete from "@components/Autocomplete/Autocomplete";
import type { AutocompleteOption } from "@components/Autocomplete/Autocomplete.types";
import Button from "@components/Button/Button";
import Input from "@components/Input/Input";
import SideDrawer from "@components/SimpleSideDrawer/SideDrawer";
import {
  CARGO_ONBOARD_IMO_HAZARD_CLASSES_MAX_LENGTH,
  CARGO_ONBOARD_QUANTITY_MAX_DECIMALS,
  CARGO_ONBOARD_QUANTITY_MAX_DIGITS,
  mapCargoOnboardItemsToGrid,
  sanitizeCargoOnboardItems,
  shouldShowCargoOnboardSection,
  type CargoOnboardFormItem,
  type CargoOnboardGridItem,
} from "@features/vessel-call/cargoOnboard";
import CargoOnboardGrid from "@features/vessel-call/components/CargoOnboardGrid";
import { FORM_FIELDS } from "@features/vessel-call/create/constants";
import type { VesselCallOutletContext } from "@features/vessel-call/create/CreateVesselCallContainer";
import { getCargoOnboardItemSchema } from "@features/vessel-call/create/schema/vesselCallFormSchema";
import { useFormattedReference } from "@hooks/useFormattedReference";
import { REFERENCE_DATA_TYPES } from "@services/referenceDataApi";
import type { UnitsOfMeasurement } from "@services/vessel-call/types";
import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router-dom";

type CargoTypeReference = {
  cargoTypeCode: string;
  cargoTypeDesc: string;
};

type DrawerState = CargoOnboardFormItem;

type DrawerErrors = Partial<Record<keyof DrawerState, string>>;

const TEST_ID_BASE = "cargo-onboard";

const isQuantityInputAllowed = (value: string) => {
  if (value === "") {
    return true;
  }

  if (!/^\d+(\.\d*)?$/.test(value)) {
    return false;
  }

  const [integerPart, decimalPart] = value.split(".");

  return (
    integerPart.length <= CARGO_ONBOARD_QUANTITY_MAX_DIGITS &&
    (decimalPart?.length ?? 0) <= CARGO_ONBOARD_QUANTITY_MAX_DECIMALS
  );
};

const emptyDrawerState = (): DrawerState => ({
  id: "",
  cargoType: "",
  cargoTypeDescription: "",
  quantity: "",
  unitOfMeasurement: "",
  unitOfMeasurementDescription: "",
  hazardousAndDangerousCargo: false,
  imoHazardClasses: "",
});

const CargoOnboard = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { formData, watch, updateFormData } = useOutletContext<VesselCallOutletContext>();
  const { setValue: setField, trigger } = formData;
  const vesselCondition = watch(FORM_FIELDS.VESSEL_CONDITION) as string | undefined;
  const cargoItems = (watch(FORM_FIELDS.CARGO_ONBOARD) as CargoOnboardFormItem[] | undefined) ?? [];
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [drawerState, setDrawerState] = useState<DrawerState>(emptyDrawerState());
  const [drawerErrors, setDrawerErrors] = useState<DrawerErrors>({});
  const [drawerTouched, setDrawerTouched] = useState<Partial<Record<keyof DrawerState, boolean>>>(
    {},
  );

  const shouldShowCargoOnboard = shouldShowCargoOnboardSection(vesselCondition);

  const { options: cargoTypes } = useFormattedReference<CargoTypeReference>(
    REFERENCE_DATA_TYPES.CARGO_TYPES,
    "bff-vessel-call",
  );
  const { options: unitsOfMeasurement } = useFormattedReference<UnitsOfMeasurement>(
    REFERENCE_DATA_TYPES.UNITS_OF_MEASUREMENT,
    "bff-vessel-call",
  );
  const cargoOnboardItemSchema = useMemo(
    () => getCargoOnboardItemSchema(t, { requireFields: true }),
    [t],
  );

  const cargoTypeOptions = useMemo<AutocompleteOption[]>(
    () =>
      cargoTypes
        .map((item) => ({
          label: item.cargoTypeDesc,
          value: item.cargoTypeCode,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [cargoTypes],
  );

  const unitOptions = useMemo<AutocompleteOption[]>(
    () =>
      unitsOfMeasurement
        .filter((item) => item.isCargoUnit)
        .map((item) => ({
          label: item.unitDescription,
          value: item.unitCode || item.unitDescription,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [unitsOfMeasurement],
  );

  const syncCargoItems = (nextItems: CargoOnboardFormItem[]) => {
    const sanitized = sanitizeCargoOnboardItems(nextItems);

    updateFormData(FORM_FIELDS.CARGO_ONBOARD, sanitized as never);

    setField(FORM_FIELDS.CARGO_ONBOARD, sanitized as never, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
    formData.clearErrors(FORM_FIELDS.CARGO_ONBOARD);
  };

  useEffect(() => {
    if (shouldShowCargoOnboard) return;

    formData.clearErrors(FORM_FIELDS.CARGO_ONBOARD);

    if (cargoItems.length === 0) return;

    syncCargoItems([]);
  }, [cargoItems.length, formData, shouldShowCargoOnboard]);

  useEffect(() => {
    if (shouldShowCargoOnboard) return;

    void trigger(FORM_FIELDS.CARGO_ONBOARD);
  }, [trigger, shouldShowCargoOnboard]);

  const openAddDrawer = () => {
    setEditingId(null);
    setDrawerErrors({});
    setDrawerTouched({});
    setDrawerState(emptyDrawerState());
    setIsDrawerOpen(true);
  };

  const openEditDrawer = (item: CargoOnboardGridItem) => {
    const current = cargoItems.find((entry) => entry.id === item.id);
    if (!current) return;

    setEditingId(current.id);
    setDrawerErrors({});
    setDrawerTouched({});
    setDrawerState({
      ...emptyDrawerState(),
      ...current,
      quantity: current.quantity ?? "",
      cargoTypeDescription: current.cargoTypeDescription ?? "",
      unitOfMeasurementDescription: current.unitOfMeasurementDescription ?? "",
      imoHazardClasses: current.imoHazardClasses ?? "",
    });
    setIsDrawerOpen(true);
  };

  const closeDrawer = () => {
    setIsDrawerOpen(false);
    setEditingId(null);
    setDrawerErrors({});
    setDrawerTouched({});
    setDrawerState(emptyDrawerState());
  };

  const validateDrawer = (state: DrawerState): DrawerErrors => {
    const result = cargoOnboardItemSchema.safeParse(state);

    if (result.success) {
      return {};
    }

    return result.error.issues.reduce<DrawerErrors>((errors, issue) => {
      const field = issue.path[0] as keyof DrawerState | undefined;
      if (!field || errors[field]) {
        return errors;
      }

      errors[field] = issue.message;
      return errors;
    }, {});
  };

  const validateDrawerField = (field: keyof DrawerState, state: DrawerState) =>
    validateDrawer(state)[field];

  const showBlockedQuantityError = (attemptedQuantity: string) => {
    const quantityError = validateDrawerField("quantity", {
      ...drawerState,
      quantity: attemptedQuantity,
    });

    setDrawerTouched((prev) => ({ ...prev, quantity: true }));
    setDrawerErrors((prev) => ({ ...prev, quantity: quantityError }));
  };

  const handleSubmit = () => {
    const errors = validateDrawer(drawerState);
    setDrawerTouched({
      cargoType: true,
      quantity: true,
      unitOfMeasurement: true,
      hazardousAndDangerousCargo: true,
      imoHazardClasses: true,
    });

    if (Object.keys(errors).length > 0) {
      setDrawerErrors(errors);
      return;
    }

    const nextItem: CargoOnboardFormItem = {
      ...drawerState,
      id: editingId ?? crypto.randomUUID(),
      quantity: drawerState.quantity,
      imoHazardClasses: drawerState.hazardousAndDangerousCargo
        ? drawerState.imoHazardClasses?.trim()
        : "",
    };

    const nextItems = editingId
      ? cargoItems.map((item) => (item.id === editingId ? nextItem : item))
      : [nextItem, ...cargoItems];

    syncCargoItems(nextItems);
    closeDrawer();
  };

  const handleRemove = (item: CargoOnboardGridItem) => {
    syncCargoItems(cargoItems.filter((entry) => entry.id !== item.id));
  };

  const gridItems = useMemo(() => mapCargoOnboardItemsToGrid(cargoItems), [cargoItems]);

  if (!shouldShowCargoOnboard) {
    return null;
  }

  const fieldError = (formData.formState.errors as Record<string, { message?: string }>)[
    FORM_FIELDS.CARGO_ONBOARD
  ]?.message;
  const isCargoTouched = Boolean(formData.formState.touchedFields?.[FORM_FIELDS.CARGO_ONBOARD]);
  const shouldShowFieldError = Boolean(
    fieldError && (isCargoTouched || formData.formState.isSubmitted),
  );

  return (
    <div className="col-span-full flex flex-col gap-4" data-testid={TEST_ID_BASE}>
      <CargoOnboardGrid
        items={gridItems}
        onEdit={openEditDrawer}
        onDelete={handleRemove}
        customSx={
          shouldShowFieldError
            ? {
                border: 1,
                borderColor: theme.palette.error.main,
              }
            : undefined
        }
      />
      <div className="flex items-center justify-between">
        <Typography color="error" variant="caption">
          {shouldShowFieldError && String(fieldError)}
        </Typography>
        <Button
          type="button"
          variant="outlined"
          testId={`${TEST_ID_BASE}-add-button`}
          onClick={openAddDrawer}
          label={
            <span className="normal-case text-sm font-medium flex items-center gap-2">
              <AddCircleOutlineIcon fontSize="small" />
              {t("vessel-call.create.cargo-onboard.buttons.add")}
            </span>
          }
        />
      </div>
      <SideDrawer
        isOpen={isDrawerOpen}
        onClose={closeDrawer}
        onCancel={closeDrawer}
        onSubmit={handleSubmit}
        title={t(
          `vessel-call.create.cargo-onboard.drawer.${editingId ? "title-edit" : "title-add"}`,
        )}
        submitLabel={t(`vessel-call.create.cargo-onboard.buttons.${editingId ? "edit" : "add"}`)}
      >
        <div className="flex flex-col gap-4 px-6 pb-6 w-[360px] max-w-full">
          <div>
            <Typography variant="subtitle2" fontWeight={600} mb={1}>
              {t("vessel-call.create.cargo-onboard.fields.cargo-type")}
            </Typography>
            <Autocomplete
              testId={`${TEST_ID_BASE}-cargo-type`}
              options={cargoTypeOptions}
              value={
                cargoTypeOptions.find((option) => option.value === drawerState.cargoType) ?? null
              }
              onChange={(option) => {
                setDrawerState((prev) => ({
                  ...prev,
                  cargoType: option?.value ? String(option.value) : "",
                  cargoTypeDescription: option?.label ?? "",
                }));
                setDrawerErrors((prev) => ({ ...prev, cargoType: undefined }));
              }}
              onBlur={() => {
                setDrawerTouched((prev) => ({ ...prev, cargoType: true }));
                setDrawerErrors((prev) => ({ ...prev, ...validateDrawer(drawerState) }));
              }}
              placeholder={t("vessel-call.create.cargo-onboard.fields.cargo-type")}
              error={Boolean(drawerTouched.cargoType && drawerErrors.cargoType)}
            />
            {drawerTouched.cargoType && drawerErrors.cargoType && (
              <FormHelperText error>{drawerErrors.cargoType}</FormHelperText>
            )}
          </div>

          <div>
            <Typography variant="subtitle2" fontWeight={600} mb={1}>
              {t("vessel-call.create.form-fields.quantity")}
            </Typography>
            <Input
              testId={`${TEST_ID_BASE}-quantity`}
              type="number"
              decimals={3}
              placeholder={t("vessel-call.create.form-fields.quantity")}
              value={drawerState.quantity}
              onChange={(event) => {
                const nextValue = event.target.value;
                if (!isQuantityInputAllowed(nextValue)) {
                  showBlockedQuantityError(nextValue);
                  return;
                }

                const nextState = {
                  ...drawerState,
                  quantity: nextValue,
                };

                setDrawerState(nextState);

                if (drawerTouched.quantity || drawerErrors.quantity) {
                  const quantityError = validateDrawerField("quantity", nextState);
                  setDrawerErrors((prev) => ({ ...prev, quantity: quantityError }));
                }
              }}
              onBlur={() => {
                setDrawerTouched((prev) => ({ ...prev, quantity: true }));
                const quantityError = validateDrawerField("quantity", drawerState);
                setDrawerErrors((prev) => ({ ...prev, quantity: quantityError }));
              }}
              slotProps={{
                htmlInput: {
                  inputMode: "decimal",
                  min: 0,
                  step: "any",
                },
              }}
              error={Boolean(drawerTouched.quantity && drawerErrors.quantity)}
            />
            {drawerTouched.quantity && drawerErrors.quantity && (
              <FormHelperText error>{drawerErrors.quantity}</FormHelperText>
            )}
          </div>

          <div>
            <Typography variant="subtitle2" fontWeight={600} mb={1}>
              {t("vessel-call.create.form-fields.unitOfMeasurement")}
            </Typography>
            <Autocomplete
              testId={`${TEST_ID_BASE}-uom`}
              options={unitOptions}
              value={
                unitOptions.find((option) => option.value === drawerState.unitOfMeasurement) ?? null
              }
              onChange={(option) => {
                setDrawerState((prev) => ({
                  ...prev,
                  unitOfMeasurement: option?.value ? String(option.value) : "",
                  unitOfMeasurementDescription: option?.label ?? "",
                }));
                setDrawerErrors((prev) => ({ ...prev, unitOfMeasurement: undefined }));
              }}
              onBlur={() => {
                setDrawerTouched((prev) => ({ ...prev, unitOfMeasurement: true }));
                setDrawerErrors((prev) => ({ ...prev, ...validateDrawer(drawerState) }));
              }}
              placeholder={t("vessel-call.create.form-fields.unitOfMeasurement")}
              error={Boolean(drawerTouched.unitOfMeasurement && drawerErrors.unitOfMeasurement)}
            />
            {drawerTouched.unitOfMeasurement && drawerErrors.unitOfMeasurement && (
              <FormHelperText error>{drawerErrors.unitOfMeasurement}</FormHelperText>
            )}
          </div>

          <div>
            <Typography variant="subtitle2" fontWeight={600} mb={1}>
              {t("vessel-call.create.form-fields.hazardousAndDangerousCargo")}
            </Typography>
            <RadioGroup
              row
              value={drawerState.hazardousAndDangerousCargo ? "yes" : "no"}
              onChange={(event) => {
                const isHazardous = event.target.value === "yes";
                setDrawerState((prev) => ({
                  ...prev,
                  hazardousAndDangerousCargo: isHazardous,
                  imoHazardClasses: isHazardous ? prev.imoHazardClasses : "",
                }));
                setDrawerErrors((prev) => ({ ...prev, imoHazardClasses: undefined }));
              }}
            >
              <FormControlLabel value="yes" control={<Radio />} label={t("common.yes")} />
              <FormControlLabel value="no" control={<Radio />} label={t("common.no")} />
            </RadioGroup>
          </div>

          {drawerState.hazardousAndDangerousCargo && (
            <div className="flex flex-col gap-1">
              <Typography variant="subtitle2" fontWeight={600} mb={1}>
                {t("vessel-call.create.form-fields.imoHazardClasses")}
              </Typography>
              <Input
                testId={`${TEST_ID_BASE}-imo-hazard-classes`}
                value={drawerState.imoHazardClasses}
                multiline
                minRows={3}
                onChange={(event) => {
                  const nextValue = event.target.value;
                  const nextState = {
                    ...drawerState,
                    imoHazardClasses: nextValue,
                  };

                  if (nextValue.length > CARGO_ONBOARD_IMO_HAZARD_CLASSES_MAX_LENGTH) {
                    const imoHazardClassesError = validateDrawerField(
                      "imoHazardClasses",
                      nextState,
                    );
                    setDrawerErrors((prev) => ({
                      ...prev,
                      imoHazardClasses: imoHazardClassesError,
                    }));
                    setDrawerTouched((prev) => ({ ...prev, imoHazardClasses: true }));
                    return;
                  }

                  setDrawerState(nextState);

                  const imoHazardClassesError = validateDrawerField("imoHazardClasses", nextState);
                  setDrawerErrors((prev) => ({
                    ...prev,
                    imoHazardClasses: imoHazardClassesError,
                  }));
                  if (imoHazardClassesError) {
                    setDrawerTouched((prev) => ({ ...prev, imoHazardClasses: true }));
                  }
                }}
                onBlur={() => {
                  setDrawerTouched((prev) => ({ ...prev, imoHazardClasses: true }));
                  const imoHazardClassesError = validateDrawerField(
                    "imoHazardClasses",
                    drawerState,
                  );
                  setDrawerErrors((prev) => ({
                    ...prev,
                    imoHazardClasses: imoHazardClassesError,
                  }));
                }}
                error={Boolean(drawerTouched.imoHazardClasses && drawerErrors.imoHazardClasses)}
              />
              {drawerTouched.imoHazardClasses && drawerErrors.imoHazardClasses && (
                <FormHelperText error>{drawerErrors.imoHazardClasses}</FormHelperText>
              )}
            </div>
          )}
        </div>
      </SideDrawer>
    </div>
  );
};

export default CargoOnboard;
